import { useMemoizedFn } from 'ahooks';
import { find, isEmpty } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  Button,
  DialogPlugin,
  Dropdown,
  Link,
  MessagePlugin,
  Space,
  Table,
  Tooltip,
} from 'tdesign-react';
import { RefreshIcon } from 'tdesign-icons-react';
import { produce } from 'immer';
import { isVoid, renderDateTime, sleep } from '@/utils/common';
import { setComponentCurrentHosts, setComponentDeployedHosts } from '@/slices/execComponentSlice';
import {
  getComponentHosts,
  getComponentK8sHost,
  getK8sHosts,
  runComponent,
} from '@/services/execComponentService';
import OperateTitle from '@/pages/Maintenance/Executive/components/OperateTitle';
import useTable from '@/hooks/useTable';
import { CLUSTER_TYPES, componentStatusOptions } from '@/utils/constant';
import SvgIcon from '@/components/SvgIcon';
import BadgeStatus from '@/components/BadgeStatus';
import { OPERATE } from '..';

export const RESULT_STATUS = {
  READY: 'READY',
  PROCESSING: 'PROCESSING',
  ALIVE: 'ALIVE',
  ERROR: 'ERROR',
  STOP: 'STOP',
};

// 结果字典 处理魔鬼状态 '0' 0 ''等
function getExecResultCodeStatus(code) {
  if (isVoid(code)) {
    return RESULT_STATUS.READY;
  }
  if (`${code}` === '0') {
    return RESULT_STATUS.PROCESSING;
  }
  if (Number(code) === 1) {
    return RESULT_STATUS.ERROR;
  }
  if (Number(code) === 2) {
    return RESULT_STATUS.ALIVE;
  }
  if (Number(code) === 3) {
    return RESULT_STATUS.STOP;
  }
}

const _options = [
  {
    content: '启动/重启',
    value: 1,
  },
  {
    content: '停止',
    value: 2,
  },
];

function ChoiceHost({
  clusterId,
  envId,
  setComponentCurrentHosts,
  hosts,
  doNextStep,
  setComponentDeployedHosts,
  deployedHosts,
  currentLib,
}) {
  const [searchParams] = useSearchParams();
  const [countDown, setCountDown] = useState(0);
  const [operateCountDown, setOperateCountDown] = useState(0);

  const processing = useRef();
  const timeoutRef = useRef();
  const operateTimeoutRef = useRef();

  const id = searchParams.get('id');
  const clusterType = Number(searchParams.get('clusterType'));

  const options = useMemo(
    () =>
      produce(_options, (draft) => {
        draft.forEach((item) => {
          if (operateCountDown > 0) {
            item.disabled = true;
            item.content = `${item.content}(${operateCountDown}秒后重试)`;
          }
        });
      }),
    [operateCountDown],
  );

  const columns = [
    {
      colKey: 'row-select',
      type: 'multiple',
      fixed: 'left',
      width: 46,
    },
    {
      title: '机器IP',
      colKey: 'serviceIp',
      cell: ({ row }) => row.serviceIp || row.hostIp,
    },
    {
      title: '当前版本',
      width: 90,
      colKey: 'currentVersion',
    },
    {
      title: '标识',
      colKey: 'lable',
    },
    {
      title: '上一个版本',
      width: 110,
      colKey: 'preVersion',
    },
    {
      title: '进程状态',
      width: 110,
      colKey: 'processStatus',
      cell: ({ row, col }) => {
        const item = row[col.colKey];
        const found = find(componentStatusOptions, { value: getExecResultCodeStatus(item) });
        return <BadgeStatus {...found} />;
      },
    },
    {
      title: '进程状态更新时间',
      colKey: 'statusUpdateTime',
      cell: renderDateTime,
      width: 180,
    },
    {
      title: '创建时间',
      colKey: 'updateTime',
      cell: renderDateTime,
      width: 180,
    },

    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      align: 'center',
      cell: ({ row }) => (
        <Space align='center' size='small'>
          <Link theme='primary' hover='color' onClick={() => doUpgrade(row)}>
            升级
          </Link>
          <Dropdown
            maxColumnWidth={200}
            options={options}
            onClick={({ value }) => clickHandler(value, row)}
          >
            <div style={{ height: 20 }}>
              <Icon type='dots-vertical' />
            </div>
          </Dropdown>
        </Space>
      ),
      width: 120,
    },
  ];

  const clickHandler = async (value, row) => {
    // 正在执行,不允许操作
    if (processing.current) {
      return;
    }
    const params = {
      clusterId,
      componentStencilId: id,
      envId,
      hostsList: String(row.id),
      ipVariableList: [],
      variableList: [],
      versionId: null,
    };

    switch (value) {
      case 1: // 启动、重启
        params.scriptBusinessEnum = 'RESTART';
        break;

      case 2: // 停止
        params.scriptBusinessEnum = 'STOP';
        break;

      default:
        break;
    }

    try {
      // const newTab = window.open('', '_blank');
      processing.current = MessagePlugin.loading({
        duration: 0,
        content: '正在执行...',
      });
      await runComponent(params);
      await sleep(2000);
      await loadData();
      MessagePlugin.success('执行成功');
      setOperateCountDown(30);
      operateTimeoutRef.current = setInterval(
        () =>
          setOperateCountDown((current) => {
            if (current > 0) {
              return current - 1;
            } else {
              clearInterval(operateTimeoutRef.current);
              loadData();
              return 0;
            }
          }),
        1000,
      );
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      MessagePlugin.close(processing.current);
      processing.current = null;
    }
  };

  const loadData = useMemoizedFn(async () => {
    setComponentCurrentHosts([]);
    let request = getComponentHosts;
    // K8s
    if (clusterType === 1) {
      request = getComponentK8sHost;
    }

    const { data } = await request({
      componentStencilId: id,
      envId,
      clusterId,
    });
    setComponentDeployedHosts(data);
    setData(data);
  });

  const { config, setData, doSearch, doRefresh } = useTable({
    columns,
    onSearch: loadData,
  });

  useEffect(() => {
    if (!isVoid(clusterId)) {
      doSearch();
    }
    return () => {
      clearInterval(timeoutRef.current);
      clearInterval(operateTimeoutRef.current);
    };
  }, [doSearch, clusterId]);

  const onSelectChange = (keys, { selectedRowData }) => {
    setComponentCurrentHosts(selectedRowData);
  };

  const createNew = () => {
    setComponentCurrentHosts([]);
    doNextStep(OPERATE.DEPLOY);
  };

  const doUpgrades = () => doNextStep(OPERATE.UPGRADE);

  const doUpgrade = (row) => {
    setComponentCurrentHosts([row]);
    doNextStep(OPERATE.UPGRADE);
  };

  const newDeployK8s = async () => {
    const {
      data: { items },
    } = await getK8sHosts({ startIndex: 1, pageSize: 99999, opsClusterId: clusterId });
    setComponentCurrentHosts(items);
    doNextStep(OPERATE.DEPLOY);
  };

  const upgradeK8s = () => {
    setComponentCurrentHosts(deployedHosts);
    doNextStep(OPERATE.UPGRADE);
  };

  const refreshStatus = async () => {
    const dialog = DialogPlugin.confirm({
      header: '重新获取进程状态？',
      theme: 'warning',
      body: '状态检查操作会在选中的机器上执行脚本逻辑，请确认是否需要执行？',
      confirmBtn: {
        content: '确定',
      },
      closeBtn: false,
      onCancel: () => dialog.hide(),
      onConfirm: async () => {
        try {
          const params = {
            clusterId,
            componentStencilId: id,
            envId,
            hostsList: deployedHosts.map((item) => item.id).join(','),
            ipVariableList: [],
            variableList: [],
            scriptBusinessEnum: 'STATUS', // 状态刷新
            versionId: null,
          };
          dialog.update({
            confirmBtn: { loading: true, content: '确定' },
          });

          await runComponent(params);
          setTimeout(() => {
            doRefresh();
          }, 5000);
          MessagePlugin.success('执行成功');
          setCountDown(30);
          timeoutRef.current = setInterval(
            () =>
              setCountDown((current) => {
                if (current > 0) {
                  return current - 1;
                } else {
                  clearInterval(timeoutRef.current);
                  return 0;
                }
              }),
            1000,
          );
          dialog.hide();
        } catch (error) {
          console.trace(error);
          throw error;
        } finally {
          dialog.update({
            confirmBtn: { loading: false, content: '确定' },
          });
        }
      },
    });
  };

  const node =
    clusterType === CLUSTER_TYPES.K8S ? (
      <Footer>
        <Space>
          <Button theme='primary' onClick={newDeployK8s}>
            新部署
          </Button>
          {deployedHosts.length > 0 && (
            <Button theme='primary' variant='outline' onClick={upgradeK8s}>
              升级
            </Button>
          )}
        </Space>
      </Footer>
    ) : (
      <Root>
        <OperateTitle
          label='第三步：'
          title='选择要执行的操作'
          operator={
            <Space>
              {!isEmpty(deployedHosts) && (
                <Tooltip content='重新获取进程状态'>
                  <Button
                    theme='default'
                    variant='outline'
                    disabled={countDown > 0}
                    onClick={refreshStatus}
                  >
                    {countDown > 0 ? `${countDown}秒后重试` : '获取进程状态'}
                  </Button>
                </Tooltip>
              )}
              <Button theme='primary' onClick={createNew}>
                新部署
              </Button>
              {!isEmpty(hosts) && (
                <Button theme='primary' variant='outline' onClick={doUpgrades}>
                  批量升级
                </Button>
              )}
              <Button variant='outline' onClick={doRefresh} icon={<RefreshIcon />} />
            </Space>
          }
        />
        <Table
          {...config}
          rowKey='id'
          maxHeight={250}
          resizable={false}
          selectedRowKeys={hosts.map((item) => item.id)}
          onSelectChange={onSelectChange}
        />
      </Root>
    );

  return !isVoid(clusterId) && currentLib && node;
}

export default connect(
  (state) => ({
    envId: state.execComponent.currentEnv.id,
    clusterId: state.execComponent.currentCluster.id,
    hosts: state.execComponent.currentHosts,
    deployedHosts: state.execComponent.deployedHosts,
    currentLib: state.execComponent.currentLib,
  }),
  {
    setComponentCurrentHosts,
    setComponentDeployedHosts,
  },
)(ChoiceHost);

const Root = styled.div`
  border-radius: 8px;
  border: 1px solid #e1e1e1;
  margin-bottom: 16px;
  overflow: hidden;
  .t-table {
    padding: 16px 20px;
  }
  .t-table__header--fixed:not(.t-table__header--multiple) > tr > th {
    background-color: transparent;
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 50px;
  padding-right: 20px;
`;

const Icon = styled(SvgIcon)`
  cursor: pointer;
  color: #194dff;
`;
